import React from 'react';

import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, Dialog, H1, H3, HStack, Spinner, Text, VStack } from '@betterment/samba-react';
import { ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';

import { AccountHighlight } from './AccountHighlight';
import { EditAssumptionsBanner, EditAssumptionsToggleTip } from './EditAssumptionsBanner';
import { GradientBorderBox } from './GradientBorderBox';
import { OpenAccountButton } from './OpenAccountButton';

import { IdempotencyProviderWithFeatureRegistry } from '../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { useAsyncEffect } from '../../../Shared/hooks/useAsyncEffect';
import AiOrb from '../assets/icon_ai_orb.svg';
import {
  CreateAccountRecommendationDocument,
  CreateAccountRecommendationMutation,
} from '../graphql/mutation.createAccountRecommendation.generated';
import { AccountRecommendationOptionDocument } from '../graphql/query.accountRecommendationOption.generated';

type RecommendationContentProps = {
  recommendation: NonNullable<CreateAccountRecommendationMutation['createAccountRecommendation']['result']>;
};

const RecommendationContent: React.FC<RecommendationContentProps> = ({ recommendation }) => {
  const { productOnboardingOption, reasoning } = recommendation;
  return (
    <VStack css={{ gap: 'space.400' }}>
      <VStack css={{ gap: 'space.100' }}>
        <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
          <Text variant="smallCaps">RECOMMENDATION</Text>
          <EditAssumptionsToggleTip />
        </HStack>
        <H1 variant="brandTitle" color="text">
          {productOnboardingOption.name}
        </H1>
        <Text variant="text100" color="text">
          {productOnboardingOption.shortDescription}
        </Text>
        <AccountHighlight text="Lorem ipsum dolor sit amet consectetur adipiscing elit quisque faucibus." />
        <AccountHighlight text="Ex sapien vitae pellentesque sem placerat in id cursus mi." />
      </VStack>
      <GradientBorderBox>
        <VStack css={{ padding: 'space.200', gap: 'space.200' }}>
          <HStack css={{ gap: 'space.100', alignItems: 'center' }}>
            <img src={AiOrb} alt="AI Icon" width="20px" height="20px" />
            <H3 variant="title100" color="text">
              Why this account?
            </H3>
          </HStack>
          <Text variant="text50" color="text" dangerouslySetInnerHTML={{ __html: reasoning }} />
          <Text variant="text25" color="text.weak">
            AI-summary based on your financial profile.{' '}
            <SambaAutoLink to="/foo" target="_blank">
              Learn more
            </SambaAutoLink>
          </Text>
        </VStack>
      </GradientBorderBox>
      <EditAssumptionsBanner />
      <OpenAccountButton option={productOnboardingOption} />
    </VStack>
  );
};

const Content: React.FC = () => {
  const { data: optionData } = useGraphQLQuery(AccountRecommendationOptionDocument, { fetchPolicy: 'cache-first' });
  const [createAccountRecommendation, { data }] = useGraphQLMutation(CreateAccountRecommendationDocument);

  const recommendationOption = optionData?.accountRecommendationOption;
  const recommendation = data?.createAccountRecommendation?.result;

  useAsyncEffect(async () => {
    if (recommendationOption) {
      const result = await createAccountRecommendation();
      if (!result.data?.createAccountRecommendation?.result) throw Error('Failed to create account recommendation');
    }
  }, [!!recommendationOption]);

  return recommendation ? (
    <RecommendationContent recommendation={recommendation} />
  ) : (
    <Box
      css={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 'space.400',
        minHeight: '500px',
      }}
    >
      <Spinner size="lg" />
    </Box>
  );
};

export const AccountRecommendationModal: React.FC = () => {
  return (
    <Dialog>
      <Dialog.Trigger asChild={true}>
        <Button
          variant="textSecondary"
          css={{ borderRadius: '20px', textDecoration: 'none', background: 'border', padding: '1px' }}
        >
          <HStack
            css={{
              px: 'space.100',
              py: 'space.50',
              borderRadius: '20px',
              background: 'background.surface',
              gap: 'space.50',
              alignItems: 'center',
            }}
          >
            <img src={AiOrb} alt="AI Icon" />
            <Text variant="text50" color="text" css={{ lineHeight: '1' }}>
              Get a personalized recommendation
            </Text>
          </HStack>
        </Button>
      </Dialog.Trigger>
      <Dialog.Content size="large" css={{ p: 'space.400', width: '60%', maxWidth: '800px' }}>
        <ErrorBoundary fallbackType="full" feature="account recommendation" owner="agents">
          <IdempotencyProviderWithFeatureRegistry featureName="createAccountRecommendation">
            <Content />
          </IdempotencyProviderWithFeatureRegistry>
        </ErrorBoundary>
      </Dialog.Content>
    </Dialog>
  );
};
